/**
 * Generic DocType List Hook
 * 
 * Provides doctype-agnostic list fetching with server-side filtering, pagination, and search.
 * All doctype-specific hooks (e.g., useInventoryListWithPagination) should use this hook.
 */

import { useQuery } from "@tanstack/react-query";
import { stableStringify } from "@/lib/utils/stableStringify";
import { validateFilters, mapFiltersToFrappe } from "@/lib/erp/filterMapper";
import { erp } from "@/lib/erp";
import { getQueryOptions } from "@/lib/config/queryConfig";
import { logger } from "@/lib/utils/logger";
import type { FilterDescriptor } from "@/lib/erp";
import type { FrappeFilterArray } from "@/lib/erp/filterMapper";
import type { FrappeListResponse } from "@/lib/frappe-client";

/**
 * Value types allowed in Frappe filters
 * Covers 99% of use cases while keeping types honest
 */
export type FrappeFilterValue = 
  | string 
  | number 
  | boolean 
  | null 
  | undefined 
  | (string | number | boolean)[];

/**
 * Parameters for generic doctype list hook
 */
export type DocTypeListParams = {
  doctype: string;
  filterDescriptors?: FilterDescriptor[];      // preferred path
  filters?: FrappeFilterArray;                 // legacy / advanced callers only
  searchQuery?: string;
  searchFields?: string[];                     // e.g. ["item_code", "item_name"]
  pageIndex?: number;
  pageSize?: number;
  orderBy?: string;                            // e.g. "posting_date desc"
  listMethodOverride?: string;                 // optional custom method path
  fields?: string[];                           // Fields to fetch from backend
};

/**
 * Generic hook for fetching any doctype list with server-side filtering, pagination, and search.
 * 
 * This hook owns the mapping from FilterDescriptor[] to Frappe format.
 * Components should pass filterDescriptors, not pre-mapped filters.
 * 
 * @param params - DocTypeListParams with doctype and filter/search/pagination options
 * @returns React Query result with data, isLoading, error
 */
export function useDocTypeListWithPagination<T>(params: DocTypeListParams) {
  const {
    doctype,
    filterDescriptors,
    filters: preMappedFilters,
    searchQuery,
    searchFields = [],
    pageIndex = 0,
    pageSize = 20,
    orderBy,
    listMethodOverride,
    fields,
  } = params;

  // 1) Build search or_filters from searchFields
  const searchOrFilters: Array<[string, string, FrappeFilterValue]> | undefined = 
    searchQuery?.trim() && searchFields.length > 0
      ? searchFields.map<[string, string, FrappeFilterValue]>((field) => [
          field,
          "like",
          `%${searchQuery.trim()}%`,
        ])
      : undefined;

  // 2) Map FilterDescriptor[] -> FrappeFilterArray
  let frappeFiltersForQuery: FrappeFilterArray | undefined;

  // Detect filterDescriptors that are not yet ready (e.g. a parent form hasn't
  // hydrated yet and passes value: undefined). We skip mapping and disable the
  // query rather than let Zod throw at render time — crashing the whole route.
  // See OPS-1531.
  const hasIncompleteDescriptor =
    !!filterDescriptors &&
    filterDescriptors.some(
      (f) => f === null || f === undefined || f.value === undefined,
    );

  if (preMappedFilters) {
    frappeFiltersForQuery = preMappedFilters;
  } else if (
    filterDescriptors &&
    filterDescriptors.length > 0 &&
    !hasIncompleteDescriptor
  ) {
    const validation = validateFilters(doctype, filterDescriptors);
    if (!validation.valid) {
      logger.warn("[useDocTypeListWithPagination] Filter validation errors", {
        doctype,
        errors: validation.errors,
      });
    }

    frappeFiltersForQuery = mapFiltersToFrappe(doctype, filterDescriptors);
  } else if (hasIncompleteDescriptor) {
    logger.warn(
      "[useDocTypeListWithPagination] Skipping query — filterDescriptor has undefined value (input not yet hydrated)",
      { doctype, filterDescriptors },
    );
  }

  // 3) Stable query key (uses stableStringify)
  const queryKey = [
    "doctype-list",
    doctype,
    {
      filters: stableStringify(frappeFiltersForQuery || []),
      orFilters: stableStringify(searchOrFilters || []),
      searchQuery: searchQuery || undefined,
      pageIndex,
      pageSize,
      orderBy: orderBy || undefined,
      listMethodOverride: listMethodOverride || undefined,
      fields: fields || undefined,
    },
  ];

  // 4) Query function: standard or overridden
  const queryFn = async () => {
    // If override is provided, call that method; otherwise use standard list path.
    if (listMethodOverride) {
      return erp.docs.docTypeListWithPaginationViaMethod<T>({
        doctype,
        filters: frappeFiltersForQuery,
        or_filters: searchOrFilters,
        pageIndex,
        pageSize,
        orderBy,
        method: listMethodOverride,
        fields,
      });
    }

    return erp.docs.docTypeListWithPagination<T>({
      doctype,
      filters: frappeFiltersForQuery,
      or_filters: searchOrFilters,
      pageIndex,
      pageSize,
      orderBy,
      fields,
    });
  };

  return useQuery<FrappeListResponse<T>>({
    queryKey,
    queryFn,
    ...getQueryOptions("list"),
    placeholderData: (previousData) => previousData,
    // Don't fire the query until filter inputs are ready. Prevents render-time
    // crashes when a parent form hasn't hydrated its formData yet (OPS-1531).
    enabled: !hasIncompleteDescriptor,
  });
}

